import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { TripCatchesClient, TripCatchRequest, TripCatchResponse } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { Equal, handleError, showNotFoundErrorNotification } from 'src/utility/utils';
import { setToNewZealandTimeZone } from 'src/utility/helper/dateHelper';
import { CatchDataModel } from 'src/views/fishingTrip/Import/DataTypes';

// Other Redux
import { getAllProducts, selectProduct } from '@store/masterData/productSlice';
import { getAllProductSpecies, ProductSpecies, selectProductSpecies } from '@store/masterData/productSpeciesSlice';
import { getAllProductQuality, ProductQuality, selectProductQuality } from '@store/masterData/productQualitySlice';
import { getAllProductSize, ProductSize, selectProductSize } from '@store/masterData/productSizeSlice';
import { getAllProductState, ProductState, selectProductState } from '@store/masterData/productStateSlice';
import { CatchDataModelFromD365 } from 'src/views/fishingTrip/details/CatchTab/Import/CatchDataTypes';


export interface TripCatch extends TripCatchResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    tripCatchList: TripCatch[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    tripCatchList: [],
};

const tripCatchSlice = createSlice({
    name: 'tripCatch',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getTripCatchListSuccess: (state, action) => {
            state.tripCatchList = action.payload;
            state.isLoading = false;
        },
        addNewTripCatchSuccess: (state, action: PayloadAction<TripCatch>) => {
            const newItem = action.payload;
            state.tripCatchList = [newItem, ...state.tripCatchList];
            toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
            state.isLoading = false;
        },
        updateTripCatchSuccess: (state, action: PayloadAction<TripCatch>) => {
            const updatedItem = action.payload;
            const index = state.tripCatchList.findIndex(item => item.tripCatchId === updatedItem.tripCatchId);
            state.tripCatchList = [...state.tripCatchList.slice(0, index), updatedItem, ...state.tripCatchList.slice(index + 1)];
            state.isLoading = false;
        },
        deleteTripCatchSuccess: (state, action: PayloadAction<number>) => {
            const deleteId = action.payload;
            state.tripCatchList = state.tripCatchList.filter(item => item.tripCatchId !== deleteId);
            state.isLoading = false;
        },
    },
});

// Reducer
export default tripCatchSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getTripCatchListSuccess,
} = tripCatchSlice.actions;

// for useSelector
export const selectTripCatch = (state: RootState) => state.tripCatch as State;

// ----------------------------------------------------------------------
export function getAllTripCatch(fishingTripId: number) {
    return async (dispatch, getState) => {
        dispatch(tripCatchSlice.actions.startLoading());
        try {
            const client = new TripCatchesClient(undefined, axios);
            const response = await client.getByFishingTrip(fishingTripId);
            dispatch(tripCatchSlice.actions.getTripCatchListSuccess(response));
            return response;
        } catch (error) {
            dispatch(tripCatchSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createTripCatch(tripCatch: TripCatch) {
    return async (dispatch, getState) => {
        dispatch(tripCatchSlice.actions.startLoading());
        try {
            const { tripCatchId, ...payload } = tripCatch;
            const request = payload as TripCatchRequest;
            if (request.tripCatchDate) {
                request.tripCatchDate = setToNewZealandTimeZone(request.tripCatchDate);
            }
            const client = new TripCatchesClient(undefined, axios);
            const response = await client.create(request);
            dispatch(tripCatchSlice.actions.addNewTripCatchSuccess(response));
        } catch (error) {
            dispatch(tripCatchSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateTripCatch(tripCatch: TripCatch, showNotification: boolean = true, showTableLoading: boolean = true) {
    return async (dispatch, getState) => {
        if (showTableLoading) {
            dispatch(tripCatchSlice.actions.startLoading());
        }
        try {
            const { tripCatchId, ...payload } = tripCatch;
            const request = payload as TripCatchRequest;
            if (request.tripCatchDate) {
                request.tripCatchDate = setToNewZealandTimeZone(request.tripCatchDate);
            }
            const client = new TripCatchesClient(undefined, axios);
            const response = await client.edit(tripCatchId, request);
            dispatch(tripCatchSlice.actions.updateTripCatchSuccess(response));
            if (showNotification) {
                toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
            }
            return true;
        } catch (error) {
            dispatch(tripCatchSlice.actions.hasError(true));
            handleError(error);
            return false;
        }
    };
}

export function deleteTripCatch(tripCatchId: number, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(tripCatchSlice.actions.startLoading());
        try {
            const client = new TripCatchesClient(undefined, axios);
            await client.delete(tripCatchId);
            dispatch(tripCatchSlice.actions.deleteTripCatchSuccess(tripCatchId));
            if (showNotification) {
                toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
            }
            return true;
        } catch (error) {
            dispatch(tripCatchSlice.actions.hasError(true));
            handleError(error);
            return false;
        }
    };
}

export function createTripCatchBatch(data: CatchDataModel[], periodIdMap: Map<string, number>) {
    return async (dispatch, getState) => {
        try {
            await dispatch(getAllProducts());
            await dispatch(getAllProductSpecies());
            await dispatch(getAllProductQuality());
            await dispatch(getAllProductSize());
            await dispatch(getAllProductState());

            const { productList } = selectProduct(getState());
            const { productSpeciesList } = selectProductSpecies(getState());
            const { productQualityList } = selectProductQuality(getState());
            const { productSizeList } = selectProductSize(getState());
            const { productStateList } = selectProductState(getState());

            const request: TripCatchRequest[] = [];
            const notFoundItems: string[] = [];
            for (const d of data) {
                const fishingTripPeriodId = periodIdMap.get(d.Period ?? '');

                const productId = productList.find(p =>
                    Equal(p.productNumber, d.ProductNumber) ||
                    Equal(p.oldProductNumber, d.ProductNumber))?.productId;
                if (!productId) {
                    notFoundItems.push(d.ProductNumber ?? 'empty product code');
                    continue;
                }

                const productSpeciesId = productSpeciesList.find((p: ProductSpecies) =>
                    Equal(p.productSpeciesCode, d.ProductSpecies))?.productSpeciesId ?? null;

                const productQualityId = productQualityList.find((p: ProductQuality) =>
                    Equal(p.productQualityCode, d.ProductQuality))?.productQualityId ?? null;

                const productSizeId = productSizeList.find((p: ProductSize) =>
                    Equal(p.productSizeCode, d.ProductSize))?.productSizeId ?? null;

                const productStateId = productStateList.find((p: ProductState) =>
                    Equal(p.productStateCode, d.ProductState))?.productStateId ?? null;

                const tripCatchDate = d.TripCatchDate ? setToNewZealandTimeZone(d.TripCatchDate.toJSDate()) : null;

                request.push({
                    fishingTripPeriodId,
                    tripCatchDate,
                    productId,
                    productSpeciesId,
                    productQualityId,
                    productSizeId,
                    productStateId,
                    amountCaughtKg: d.CaughtKg,
                    ratePerKg: d.RatePerKg,
                } as TripCatchRequest);
            }
            const client = new TripCatchesClient(undefined, axios);
            await client.createBatch(request);
            if (notFoundItems.length > 0) {
                showNotFoundErrorNotification(notFoundItems.join(','));
            }
        } catch (error) {
            handleError(error);
        }
    };
}

export const fetchD365Catch = async (fishingTripNumber: string) => {
    try {
        const client = new TripCatchesClient(undefined, axios);
        const response = await client.fetchD365TripCatch(fishingTripNumber);
        return response;
    } catch (error) {
        handleError(error);
    }
}

export function createTripCatchBatchFromD365(data: CatchDataModelFromD365[], fishingTripPeriodId: number) {
    return async (dispatch, getState) => {
        try {
            await dispatch(getAllProducts());
            await dispatch(getAllProductSpecies());
            await dispatch(getAllProductQuality());
            await dispatch(getAllProductSize());
            await dispatch(getAllProductState());

            const { productList } = selectProduct(getState());
            const { productSpeciesList } = selectProductSpecies(getState());
            const { productQualityList } = selectProductQuality(getState());
            const { productSizeList } = selectProductSize(getState());
            const { productStateList } = selectProductState(getState());

            const request: TripCatchRequest[] = [];
            const notFoundItems: string[] = [];
            for (const d of data) {
                const productId = productList.find(p =>
                    Equal(p.productNumber, d.ProductNumber) ||
                    Equal(p.oldProductNumber, d.ProductNumber))?.productId;
                if (!productId) {
                    notFoundItems.push(d.ProductNumber ?? 'empty product code');
                    continue;
                }

                const productSpeciesId = productSpeciesList.find((p: ProductSpecies) =>
                    Equal(p.productSpeciesCode, d.ProductSpecies))?.productSpeciesId ?? null;

                const productQualityId = productQualityList.find((p: ProductQuality) =>
                    Equal(p.productQualityCode, d.ProductQuality))?.productQualityId ?? null;

                const productSizeId = productSizeList.find((p: ProductSize) =>
                    Equal(p.productSizeCode, d.ProductSize))?.productSizeId ?? null;

                const productStateId = productStateList.find((p: ProductState) =>
                    Equal(p.productStateCode, d.ProductState))?.productStateId ?? null;

                const tripCatchDate = d.TripCatchDate ? setToNewZealandTimeZone(d.TripCatchDate.toJSDate()) : null;

                request.push({
                    fishingTripPeriodId,
                    tripCatchDate,
                    productId,
                    productSpeciesId,
                    productQualityId,
                    productSizeId,
                    productStateId,
                    amountCaughtKg: d.CaughtKg,
                    ratePerKg: d.RatePerKg,
                } as TripCatchRequest);
            }
            const client = new TripCatchesClient(undefined, axios);
            await client.createBatch(request);
            if (notFoundItems.length > 0) {
                showNotFoundErrorNotification(notFoundItems.join(','));
            }
        } catch (error) {
            handleError(error);
        }
    };
}